import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { ConsultCTA } from "@/components/brand/ConsultCTA";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { supabase } from "@/integrations/supabase/client";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/solutions")({
  head: () =>
    seo({
      title: "Solutions · Currency Thoughts",
      description:
        "Family Protection, Generational Wealth, Retirement Strategy, and Business Continuity — how our Family Wealth Strategists serve your family.",
      canonical: "/solutions",
    }),
  component: SolutionsPage,
});

const SECTIONS = [
  { id: "family-protection", label: "Family Protection" },
  { id: "generational-wealth", label: "Generational Wealth" },
  { id: "retirement-strategy", label: "Retirement Strategy" },
  { id: "business-continuity", label: "Business Continuity" },
] as const;

function SolutionsPage() {
  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <Hero />
        <SectionNav />
        <FamilyProtection />
        <GenerationalWealth />
        <RetirementStrategy />
        <BusinessContinuity />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="bg-navy-900 text-cream-50">
      <div className="mx-auto max-w-7xl px-5 py-24 text-center md:px-10 md:py-32">
        <Eyebrow color="cream">Solutions</Eyebrow>
        <h1 className="mx-auto mt-6 max-w-3xl font-display font-semibold text-cream-50 text-[40px] leading-[1.1] md:text-[56px]">
          Built for every stage of your family's story.
        </h1>
        <p className="mx-auto mt-6 max-w-[720px] font-sans text-[17px] leading-[1.65] text-cream-50/80">
          We are a Family Wealth Strategy Firm. Our Family Wealth Strategists work on four
          questions every household eventually has to answer — and stay in the relationship long
          enough to help you answer them.
        </p>
      </div>
    </section>
  );
}

/* ---------- Sticky section nav ---------- */
function SectionNav() {
  const [active, setActive] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: [0, 0.25, 0.5, 1] },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div
      className="sticky z-10 bg-cream-50"
      style={{
        top: "80px",
        borderTop: "1px solid rgba(168, 139, 92, 0.4)",
        borderBottom: "1px solid rgba(168, 139, 92, 0.4)",
      }}
    >
      <nav className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-2 px-5 py-3 md:px-10">
        {SECTIONS.map((s) => {
          const isActive = active === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="font-sans text-[13px] transition-colors hover:text-bronze-600"
              style={{
                color: isActive ? "var(--color-navy-800, #0A3369)" : "#374151",
                textDecoration: isActive ? "underline" : "none",
                textUnderlineOffset: "6px",
                textDecorationThickness: "2px",
                textDecorationColor: "var(--color-bronze-600, #A88B5C)",
                fontWeight: isActive ? 600 : 500,
              }}
            >
              {s.label}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

/* ---------- Section primitives ---------- */
function SectionShell({
  id,
  tone,
  children,
}: {
  id: string;
  tone: "white" | "cream";
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className={tone === "white" ? "bg-white" : "bg-cream-50"}
      style={{ scrollMarginTop: "140px" }}
    >
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-10">{children}</div>
    </section>
  );
}

function SectionHeader({
  eyebrow,
  titleHtml,
  lead,
}: {
  eyebrow: string;
  titleHtml: string;
  lead: string;
}) {
  return (
    <header className="max-w-[820px]">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2
        className="mt-5 font-display font-semibold text-navy-900 text-[36px] leading-[1.15] md:text-[44px]"
        dangerouslySetInnerHTML={{ __html: titleHtml }}
      />
      <p className="mt-5 max-w-[720px] font-sans text-[16px] leading-[1.65] text-charcoal-500">
        {lead}
      </p>
    </header>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-bronze-400/25 px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-800">
      {children}
    </span>
  );
}

interface CardProps {
  eyebrow: string;
  title: string;
  body: string;
  pill?: string;
  cta?: { label: string; to: string };
}

function SolutionCard({ eyebrow, title, body, pill, cta }: CardProps) {
  return (
    <article
      className="flex h-full flex-col bg-white p-8 shadow-[0_6px_24px_-12px_rgba(168,139,92,0.18)]"
      style={{ borderTop: "4px solid var(--color-navy-800, #0A3369)" }}
    >
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
        {eyebrow}
      </p>
      <h3 className="mt-3 font-display font-semibold text-navy-900 text-[24px] leading-[1.2]">
        {title}
      </h3>
      <p className="mt-4 font-sans text-[15px] leading-[1.65] text-charcoal-900">
        {body}
      </p>
      {pill && <div className="mt-5">{<Pill>{pill}</Pill>}</div>}
      {cta && (
        <div className="mt-6 pt-5" style={{ borderTop: "1px solid rgba(168, 139, 92, 0.3)" }}>
          <Link
            to={cta.to}
            className="font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800"
          >
            {cta.label} →
          </Link>
        </div>
      )}
    </article>
  );
}

function ReadMore({ links }: { links: Array<{ label: string; to: string }> }) {
  return (
    <p className="mt-8 font-sans text-[13px] text-charcoal-500">
      <span className="font-semibold uppercase tracking-[0.18em] text-bronze-600">
        Read more:
      </span>{" "}
      {links.map((l, i) => (
        <span key={l.to}>
          <Link to={l.to} className="text-navy-800 underline decoration-bronze-600/40 underline-offset-2 hover:text-bronze-600">
            {l.label}
          </Link>
          {i < links.length - 1 && <span className="mx-2 text-bronze-600">·</span>}
        </span>
      ))}
    </p>
  );
}


const CONSULT_CLOSER =
  "The specific strategies and solutions that fit your family are discussed in a personal consultation, where we can review your actual situation.";

function Closer() {
  return (
    <p className="mt-8 max-w-[720px] font-sans text-[15px] leading-[1.65] text-charcoal-500 italic">
      {CONSULT_CLOSER}
    </p>
  );
}

/* ---------- Section 1 — Family Protection ---------- */
function FamilyProtection() {
  return (
    <SectionShell id="family-protection" tone="white">
      <SectionHeader
        eyebrow="01 — Family Protection"
        titleHtml={`What happens to the household <em class="italic text-bronze-600">if income stops.</em>`}
        lead="Most households run on one or two paychecks. We map what the family needs each month, how long it would need it, and where that money would actually come from if an earner were suddenly gone."
      />
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        <SolutionCard
          eyebrow="The Question"
          title="Could the household keep going?"
          body="Housing, food, childcare, and debt service do not pause. We work out the real monthly requirement and how many years it has to be covered."
        />
        <SolutionCard
          eyebrow="What We Review"
          title="Existing coverage and true exposure"
          body="Employer benefits, anything already in place, and the gap between what a family assumes is covered and what is."
        />
      </div>
      <Closer />
      <ReadMore
        links={[
          { label: "What protection actually does", to: "/academy/tracks/protection-101/lessons/what-insurance-actually-does" },
          { label: "The DIME method", to: "/academy/tracks/protection-101/lessons/how-much-coverage-dime" },
        ]}
      />
    </SectionShell>
  );
}

/* ---------- Section 2 — Generational Wealth ---------- */
function GenerationalWealth() {
  return (
    <SectionShell id="generational-wealth" tone="cream">
      <SectionHeader
        eyebrow="02 — Generational Wealth"
        titleHtml={`How value moves to the next generation <em class="italic text-bronze-600">intact.</em>`}
        lead="Most family wealth does not survive the handoff. The loss usually comes from unclear instructions, unfunded structures, and heirs who were never taught what they were receiving."
      />
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        <SolutionCard
          eyebrow="The Question"
          title="Who receives what, and how?"
          body="Beneficiary records, titling, and estate documents decide the outcome — often overriding what the family believed the plan was."
        />
        <SolutionCard
          eyebrow="What We Review"
          title="Structure and readiness"
          body="Whether the documents match the intent, whether the structures are actually funded, and whether the next generation has been prepared to steward what arrives."
        />
      </div>
      <Closer />
      <ReadMore
        links={[
          { label: "Revocable vs irrevocable trusts", to: "/academy/tracks/legacy/lessons/revocable-vs-irrevocable-trust" },
          { label: "Beneficiary designations", to: "/academy/tracks/legacy/lessons/beneficiaries-the-quiet-document" },
        ]}
      />
    </SectionShell>
  );
}

/* ---------- Section 3 — Retirement Strategy ---------- */
function RetirementStrategy() {
  return (
    <SectionShell id="retirement-strategy" tone="white">
      <SectionHeader
        eyebrow="03 — Retirement Strategy"
        titleHtml={`Turning accumulated value into <em class="italic text-bronze-600">reliable income.</em>`}
        lead="Retirement is a sequence of decisions — when to claim Social Security, which accounts to draw from first, and how to make what was saved last as long as the household does."
      />
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        <SolutionCard
          eyebrow="The Question"
          title="What does the household spend, and where does it come from?"
          body="Income need in the first decade after work looks different from the last. We map the sequence, not a single number."
        />
        <SolutionCard
          eyebrow="What We Review"
          title="Timing and order of withdrawals"
          body="Claiming decisions, tax treatment of each source, and the risk of drawing down in the wrong order."
        />
      </div>
      <Closer />
      <ReadMore
        links={[
          { label: "The three buckets of retirement income", to: "/academy/tracks/retirement/lessons/three-buckets-of-retirement" },
          { label: "Social Security timing", to: "/academy/tracks/retirement/lessons/social-security-timing" },
        ]}
      />
    </SectionShell>
  );
}

/* ---------- Section 4 — Business Continuity ---------- */
function BusinessContinuity() {
  return (
    <SectionShell id="business-continuity" tone="cream">
      <SectionHeader
        eyebrow="04 — Business Continuity"
        titleHtml={`Keeping a business whole through an <em class="italic text-bronze-600">owner's exit.</em>`}
        lead="Owners carry risks a household plan does not address — the loss of a key person, an ownership transition nobody documented, and an exit that has no funding behind it."
      />
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        <SolutionCard
          eyebrow="The Question"
          title="What happens to the business on the day the owner stops?"
          body="Planned or unplanned, the answer is usually written somewhere — or nowhere at all."
        />
        <SolutionCard
          eyebrow="What We Review"
          title="Agreements, key people, and funding"
          body="Whether a transition agreement exists, who the business cannot operate without, and whether the transition has money behind it."
        />
      </div>
      <Closer />
    </SectionShell>
  );
}

/* ---------- CTA ---------- */
function CTA() {
  return (
    <section className="bg-cream-50">
      <div className="mx-auto max-w-[720px] px-5 py-20 text-center md:px-10">
        <h2 className="font-display font-semibold text-navy-900 text-[36px] leading-[1.15] md:text-[44px]">
          Not sure where to start?
        </h2>
        <p className="mx-auto mt-5 max-w-[560px] font-sans text-[16px] leading-[1.65] text-charcoal-500">
          Every family's situation is different. Let's walk through yours.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <ConsultCTA />
          <SecondaryButton asChild size="lg" className="border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-cream-50 hover:border-navy-800">
            <Link to="/tools/family-snapshot">Take the Family Snapshot</Link>
          </SecondaryButton>
        </div>
      </div>
    </section>
  );
}